/*
 * HONEYWELL INC.,
 * COPYRIGHT (c) 2022
 * ALL RIGHTS RESERVED
 *
 * This software is a copyrighted work and/or information protected as a trade secret.
 * Legal rights of Honeywell Inc. in this software is distinct from ownership of any medium
 * in which the software is embodied. Copyright or trade secret notices included must be
 * reproduced in any copies authorized by Honeywell Inc.
 *
 */

import React from 'react';
import './MeterSavingsTableTheme.scss';
import {
  formatCurrency,
  getAbsoluteValue,
  getMeterName,
  getUtilityImage
} from '../Utils/Utils';
import {
  KPI_PERCENTAGE_DECIMAL_PRECISION,
  KPI_VALUE_DECIMAL_PRECISION,
  MAGIC_VALUE,
  UtilityMeterField,
  UtilityType
} from '../Utils/Constants';
import { ICellData } from '@scuf/datatable/dist/components/DataTable/IDataTableInterfaces';
import CommonProgressBar from '../../shared/CommonProgressBar/CommonProgressBar';
import { getPercentageColor } from '../../DetailKPIWidgets/PortfolioEnergyUsageViewDetails/PremiumSubscription/AllUtilitiesDetails/BottomSection/TableColumnRenderUtils';
import TruncateText from '../TruncatedText/TruncateText';
import { IAny } from '../../DashboardKPIWidgets/IEnergyManagementProps';
import { Icon } from '@scuf/common';
import { DataTable } from '@scuf/datatable';
import { IDataTableColumn } from './IMeterSavingsDetailTableProps';
import { getConvertedValue } from '../Utils/unitHandling/unitHandling';

export const cellDataRenderer = (
  cellData: ICellData,
  utilityCostCurrency: string | undefined
) => {
  const { value, field, rowData } = cellData;
  const currencyconverter = formatCurrency(value, utilityCostCurrency);

  if (field === UtilityMeterField.totalActualConsumption) {
    return (
      <span className="score-category-meterdata">
        <span className="score-category-percent">
          <div className="measurementunit">
            {getConvertedValue(
              rowData.meterType,
              rowData.fromUnit,
              value,
              rowData.actualUnit
            )?.Value?.toFixed(KPI_VALUE_DECIMAL_PRECISION)}
          </div>
          {rowData.actualUnit}
        </span>
        <span className="score-category-progress">
          <CommonProgressBar
            actual={rowData.totalActualConsumption}
            previousPeriod={rowData.previousPeriodConsumption}
            savings={rowData.consumptionSavings}
            savingsPercentage={rowData.percentageConsumptionSaving}
            progessBarRelativeWidth={rowData.progessBarRelativeWidth}
          />
        </span>
      </span>
    );
  }
  if (field === UtilityMeterField.consumptionSavings) {
    return renderConsumtionSaving(rowData, value);
  }
  if (field === UtilityMeterField.costSavings) {
    return renderCostSavings(rowData, value, currencyconverter);
  }
  if (field === UtilityMeterField.type) {
    return (
      <span className="score-category-meterdata-meter ">
        <span className="score-category-Badge">
          {getUtilityImage(rowData.type)}
        </span>
        <span className="score-category-percent">{getMeterName(value)}</span>
      </span>
    );
  }

  if (field === UtilityMeterField.previousPeriodCost) {
    return renderPrevPeriodCostCell(currencyconverter);
  }
  if (field === UtilityMeterField.totalActualCost) {
    return renderTotalActualCost(currencyconverter, rowData);
  }
  if (field === UtilityMeterField.previousPeriodConsumption) {
    return renderPrevPeriodConsumption(value, rowData);
  }
  return <></>;
};

export const rowcellDataRenderer = (
  cellData: ICellData,
  utilityCostCurrency: string | undefined,
  utilityType: string
) => {
  const { value, field, rowData } = cellData;
  const currencyconverter = formatCurrency(value, utilityCostCurrency);

  if (field === UtilityMeterField.totalActualConsumption) {
    return (
      <span className="score-category-meterdata">
        <span className="score-category-percent">
          <div className="measurementunit">
            {
              getConvertedValue(
                rowData.meterType,
                rowData.fromUnit,
                value,
                rowData.actualUnit
              )?.Value
            }
          </div>
          {rowData.actualUnit}
        </span>
        <span className="score-category-progress">
          <CommonProgressBar
            actual={rowData.totalActualConsumption}
            previousPeriod={rowData.previousPeriodConsumption}
            savings={rowData.consumptionSavings}
            savingsPercentage={rowData.totalConsumptionSavingsPercentage}
            progessBarRelativeWidth={rowData.progessBarRelativeWidth}
          />
        </span>
      </span>
    );
  }
  if (field === UtilityMeterField.costSavings) {
    return renderCostSavings(rowData, value, currencyconverter);
  }
  if (field === UtilityMeterField.consumptionSavings) {
    return renderConsumtionSaving(rowData, value);
  }
  if (field === UtilityMeterField.type) {
    const MeterNumber = rowData.meterNumber;
    if (utilityType === UtilityType.All) {
      return (
        <span className="score-category-meterdata-meter ">
          <span className="score-category-percent">
            <div>{rowData.meterName} </div>
          </span>
        </span>
      );
    } else {
      return (
        <span className="score-category-meterdata-meter ">
          <span className="score-category-percent">
            {value <= MAGIC_VALUE.FIVE ? (
              <div className="measurementunit">
                <TruncateText text={MeterNumber} />
              </div>
            ) : (
              <div>{MeterNumber}</div>
            )}
          </span>
        </span>
      );
    }
  }
  if (field === UtilityMeterField.previousPeriodCost) {
    return renderPrevPeriodCostCell(currencyconverter);
  }
  if (field === UtilityMeterField.previousPeriodConsumption) {
    return renderPrevPeriodConsumption(value, rowData);
  }
  if (field === UtilityMeterField.totalActualCost) {
    return renderTotalActualCost(currencyconverter, rowData);
  }
  return <></>;
};

export const renderActualConsumCell = (
  cellData: ICellData,
  utilityMeasurementUnit: string | undefined,
  originalUnit?: string | undefined,
  utilityType?: string
) => {
  const { value, rowData } = cellData;
  return (
    <span className="score-category-meterdata">
      <span className="score-category-percent">
        <div className="measurementunit">
          {
            getConvertedValue(
              utilityType ? utilityType : 'Electricity',
              originalUnit ? originalUnit : 'kilowatt hour',
              value,
              rowData.actualUnit
            )?.Value
          }
        </div>
        {rowData.actualUnit}
      </span>
      <span className="score-category-progress">
        <CommonProgressBar
          actual={rowData.totalActualConsumption}
          previousPeriod={rowData.previousPeriodConsumption}
          savings={rowData.totalConsumptionSavings}
          savingsPercentage={rowData.totalConsumptionSavingsPercentage}
          progessBarRelativeWidth={rowData.progessBarRelativeWidth}
        />
      </span>
    </span>
  );
};
export const expanderTemplate = (
  data: ICellData,
  expandedRows: IAny[],
  setexpandedRows: React.Dispatch<React.SetStateAction<IAny>>
) => {
  const open =
    expandedRows.findIndex(
      (item) => JSON.stringify(item) === JSON.stringify(data.rowData)
    ) >= 0;
  return (
    <div
      onClick={() =>
        toggleExpand(data.rowData, open, expandedRows, setexpandedRows)
      }
    >
      <Icon
        root="common"
        size="small"
        name={open ? 'caret-down' : 'caret-right'}
      />
    </div>
  );
};

const toggleExpand = (
  data: IAny,
  open: boolean,
  expandedRows: IAny[],
  setexpandedRows: React.Dispatch<React.SetStateAction<IAny>>
) => {
  const expanded = [...expandedRows];
  if (open) {
    let index = expanded.findIndex(
      (item) => JSON.stringify(item) === JSON.stringify(data)
    );
    expanded.splice(index, 1);
  } else {
    expanded.push(data);
  }
  setexpandedRows(expanded);
};

export const renderTableColumns = (
  columnDetails: IDataTableColumn[],
  renderColumn: (cellData: ICellData) => JSX.Element
) => {
  return columnDetails.map((column: IDataTableColumn) => {
    return (
      <DataTable.Column
        key={column.field}
        className={column.className}
        field={column.field}
        header={column.header}
        sortable={column.sortable}
        renderer={renderColumn}
        initialWidth={column.initialWidth}
      />
    );
  });
};

const renderPrevPeriodCostCell = (currencyconverter: string) => {
  return (
    <span className="score-category-meterdata-meter">
      <span className="score-category-percent">{currencyconverter}</span>
    </span>
  );
};

const renderPrevPeriodConsumption = (value: IAny, rowData: IAny) => {
  return (
    <span className="score-category-meterdata-meter">
      <div className="consumptionvalue">
        {getAbsoluteValue(
          Number(
            getConvertedValue(
              rowData.meterType,
              rowData.fromUnit,
              value,
              rowData.baselineUnit
            )?.Value?.toFixed(KPI_VALUE_DECIMAL_PRECISION)
          )
        )}
      </div>
      {rowData.baselineUnit}
    </span>
  );
};
const renderTotalActualCost = (currencyconverter: string, rowData: IAny) => {
  return (
    <span className="score-category-meterdata">
      <span className="score-category-percent">
        <div className="measurementunit">{currencyconverter}</div>
      </span>
      <span className="score-category-progress">
        <CommonProgressBar
          actual={rowData.totalActualCost}
          previousPeriod={rowData.previousPeriodCost}
          savings={rowData.costSavings}
          savingsPercentage={rowData.percentageCostSavings}
          progessBarRelativeWidth={rowData.progessBarRelativeWidthCost}
        />
      </span>
    </span>
  );
};

const renderConsumtionSaving = (rowData: IAny, value: number) => {
  return value !== null ? (
    <span className="score-category-meterdata-meter">
      <span
        className="savings-badge"
        style={{
          color: getPercentageColor(
            Number(
              getConvertedValue(
                rowData.meterType,
                rowData.fromUnit,
                value,
                rowData.changeUnit
              )?.Value
            )
          )
        }}
      >
        {getAbsoluteValue(
          Number(
            getConvertedValue(
              rowData.meterType,
              rowData.fromUnit,
              value,
              rowData.changeUnit
            )?.Value
          )
        ).toFixed(KPI_VALUE_DECIMAL_PRECISION)}
        {' ' +
          rowData.changeUnit +
          '(' +
          Math.abs(rowData.percentageConsumptionSaving).toFixed(
            KPI_PERCENTAGE_DECIMAL_PRECISION
          ) +
          '%)'}
      </span>
    </span>
  ) : (
    <>--</>
  );
};
const renderCostSavings = (
  rowData: IAny,
  value: number,
  currencyconverter: string
) => {
  return value !== null ? (
    <span
      style={{ display: 'flex' }}
      className="score-category-meterdata-meter"
    >
      <div
        className="savings-badge"
        style={{
          color: getPercentageColor(value)
        }}
      >
        {currencyconverter}
      </div>
      <div
        className="savings-badge"
        style={{
          color: getPercentageColor(value)
        }}
      >
        {'(' +
          Math.abs(rowData.percentageCostSavings).toFixed(
            KPI_PERCENTAGE_DECIMAL_PRECISION
          ) +
          '%)'}
      </div>
    </span>
  ) : (
    <>--</>
  );
};
